import React, { useState, useEffect, useContext } from "react";
import { MyContext } from "../../../context/MyContext";
import CustomChart from "../../../components/CustomChart";
import Loading from "../../../components/Loading";
import Btn from "../../../components/btn";
import { Chevron } from "../../../assets/svgs/icons";
import iconTeacherTop from "../../../assets/icons/icon_teacher_top.png";
import { downloadElementAsPdf } from "../../../utils/downloadPdf";

// 뱃지 스타일 (피그마 디자인 기준)
const BADGE_STYLES = {
  우수: {
    backgroundColor: "#f0eafe",
    color: "#6d2ef2",
    border: "1px solid #6d2ef2",
  },
  보통: {
    backgroundColor: "#ecf2fe",
    color: "#417af5",
    border: "1px solid #417af5",
  },
  노력: {
    backgroundColor: "#ffecf3",
    color: "#ff4084",
    border: "1px solid #ff4084",
  },
};

/**
 * 평가 등급 계산 (10점 만점 기준)
 */
const getEvaluation = (score: number): "우수" | "보통" | "노력" => {
  if (score >= 8) return "우수";
  if (score >= 5) return "보통";
  return "노력";
};

/**
 * API level (A/B/C)을 한글 등급으로 변환
 */
const convertLevel = (level: string): "우수" | "보통" | "노력" => {
  if (level === "A") return "우수";
  if (level === "B") return "보통";
  return "노력";
};

/**
 * 배열의 score 평균 계산
 */
const calculateAvgScore = (
  arr: Array<{ score: number; level: string }> | undefined
): number => {
  if (!arr || arr.length === 0) return 0;
  const sum = arr.reduce((acc, item) => acc + (item.score || 0), 0);
  return Math.round(sum / arr.length);
};

interface InterviewResultV2Props {
  interview: any;
  messages: any[];
  onRestart?: () => void;
  onGenerateResult?: () => void;
}

/**
 * Socrates V2 - 인터뷰 결과 (Figma 디자인 기반)
 * 섹션: 헤더, 기본정보, 종합결과, 결과요약, 질문과 답변 상세
 */
const InterviewResultV2: React.FC<InterviewResultV2Props> = ({
  interview,
  messages,
  onGenerateResult,
}) => {
  const { user } = useContext(MyContext);

  // PDF 다운로드 함수 (html2canvas + jsPDF)
  const handleDownload = () => {
    const title = interview?.interviewSubject
      ? `${interview.interviewSubject} 면접 결과`
      : "소크라테스 면접 결과";
    return downloadElementAsPdf("pdf-result-v2", title);
  };

  // 다운로드 핸들러를 window 객체에 저장하여 외부(헤더 버튼)에서 접근 가능하게 함
  useEffect(() => {
    (window as any).__socratesDownloadHandler = handleDownload;
    return () => {
      delete (window as any).__socratesDownloadHandler;
    };
  }, [interview]);

  // interview.result가 있으면 실제 데이터 사용, 없으면 로딩 상태
  const apiResult = interview?.result;
  const isLoading = !apiResult;

  // questionAnswerPairs 기반 통계 계산
  const qaPairs = apiResult?.questionAnswerPairs || [];
  const totalQuestionCount = qaPairs.length;
  const validAnswers = qaPairs.filter(
    (pair: { answer: string }) =>
      pair.answer && pair.answer !== "답변 시간 초과"
  );
  const totalAnswerCount = validAnswers.length;

  // answer 글자 수 평균 계산 (유효한 답변만)
  const avgAnswerLength =
    validAnswers.length > 0
      ? Math.round(
          validAnswers.reduce(
            (acc: number, pair: { answer: string }) =>
              acc + (pair.answer?.length || 0),
            0
          ) / validAnswers.length
        )
      : 0;

  // assessmentCriteria에서 각 항목의 평균 점수 계산
  const criteria = apiResult?.assessmentCriteria || {};
  const fluencyAvg = calculateAvgScore(criteria["유창성"]);
  const logicAvg = calculateAvgScore(criteria["논리성"]);
  const psAvg = calculateAvgScore(criteria["문제해결"]);
  const accuracyAvg = calculateAvgScore(criteria["정확성"]);
  const commuAvg = calculateAvgScore(criteria["의사소통"]);

  // API 결과 데이터를 UI에 맞게 변환
  const result = apiResult
    ? {
        user_name:
          apiResult.userName || interview?.userName || user?.name || "사용자",
        apply_org:
          apiResult.targetInstitution || interview?.targetInstitution || "-",
        inter_area:
          apiResult.interviewSubject || interview?.interviewSubject || "-",
        form_kind:
          apiResult.interviewType || interview?.interviewType || "일반 면접",
        tt_latency: apiResult.totalLatency || 0,
        tt_ques_cnt: totalQuestionCount,
        tt_ans_cnt: totalAnswerCount,
        avg_latency: apiResult.avgLatency || 0,
        avg_words: avgAnswerLength,
        top_word_list: apiResult.topWordList || [],
        fluency_sc: fluencyAvg,
        fluency_eva: getEvaluation(fluencyAvg),
        logic_sc: logicAvg,
        logic_eva: getEvaluation(logicAvg),
        ps_sc: psAvg,
        ps_eva: getEvaluation(psAvg),
        accuracy_sc: accuracyAvg,
        accuracy_eva: getEvaluation(accuracyAvg),
        commu_sc: commuAvg,
        commu_eva: getEvaluation(commuAvg),
        created_at:
          apiResult.createdAt ||
          interview?.createdAt ||
          new Date().toISOString(),
      }
    : null;

  // 피드백 목록 추출 (name, feedback, score, level, evidence 함께)
  const getFeedbackList = (
    arr: Array<{
      name: string;
      feedback: string;
      score: number;
      level: string;
      evidence?: string[];
    }>
  ) => {
    if (!arr || arr.length === 0) return [];
    return arr
      .map((item) => ({
        name: item.name,
        feedback: item.feedback,
        score: item.score,
        level: item.level,
        evidence: item.evidence || [],
      }))
      .filter((item) => item.feedback);
  };

  // 차트용 데이터 배열
  const arrData = result
    ? [
        {
          target: "유창성",
          score: result.fluency_sc,
          evaluation: result.fluency_eva,
          feedbacks: getFeedbackList(criteria["유창성"]),
        },
        {
          target: "논리성",
          score: result.logic_sc,
          evaluation: result.logic_eva,
          feedbacks: getFeedbackList(criteria["논리성"]),
        },
        {
          target: "문제해결",
          score: result.ps_sc,
          evaluation: result.ps_eva,
          feedbacks: getFeedbackList(criteria["문제해결"]),
        },
        {
          target: "정확성",
          score: result.accuracy_sc,
          evaluation: result.accuracy_eva,
          feedbacks: getFeedbackList(criteria["정확성"]),
        },
        {
          target: "의사소통",
          score: result.commu_sc,
          evaluation: result.commu_eva,
          feedbacks: getFeedbackList(criteria["의사소통"]),
        },
      ]
    : [];

  const [chartData, setChartData] = useState<
    Array<{ subject: string; score: number; fullMark: number }>
  >([]);

  // 종합결과 평가 항목 토글 상태
  const [expandedEval, setExpandedEval] = useState<Record<string, boolean>>({});

  const today = result ? new Date(result.created_at) : new Date();

  const config = {
    type: "radar" as const,
    data: arrData,
    options: {
      elements: {
        line: {
          borderWidth: 3,
        },
      },
      scales: {
        r: {
          suggestedMin: 0,
          suggestedMax: 10,
        },
      },
    },
  };

  const transformResultToData = (
    data: Array<{ target: string; score: number }>
  ) => {
    return data.map((item) => ({
      subject: item.target,
      score: item.score,
      fullMark: 10,
    }));
  };

  useEffect(() => {
    if (arrData.length > 0 && arrData[0].target !== null) {
      const transformedData = transformResultToData(arrData);
      setChartData(transformedData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiResult]);

  const toggleEval = (target: string) => {
    setExpandedEval((prev) => ({
      ...prev,
      [target]: !prev[target],
    }));
  };

  // 날짜 포맷팅
  const formatDate = () => {
    return `${today.getMonth() + 1}월 ${today.getDate()}일 모의 면접 결과`;
  };

  // 시간 포맷팅 (HH:MM:SS)
  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(
      2,
      "0"
    )}:${String(secs).padStart(2, "0")}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loading />
      </div>
    );
  }

  if (!result) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 min-h-[400px]">
        <p className="text-[#888] text-[14px]">
          생성된 결과가 없습니다. 잠시후 다시 시도해주세요.
        </p>
        <Btn.Secondary onClick={onGenerateResult}>결과 다시 생성</Btn.Secondary>
      </div>
    );
  }

  // 기본정보 테이블 데이터
  const basicInfoData = [
    {
      label: "면접일시",
      value: `${today.getFullYear()}년 ${
        today.getMonth() + 1
      }월 ${today.getDate()}일 ${today.getHours()}시 ${today.getMinutes()}분`,
    },
    { label: "성명", value: result.user_name },
    { label: "면접유형", value: result.form_kind },
    { label: "지원기관", value: result.apply_org },
    { label: "관심영역", value: result.inter_area },
    {
      label: "업로드파일",
      value: interview?.personalStatement?.fileName || "-",
    },
  ];

  return (
    <>
      <div
        id="pdf-result-v2"
        className="flex flex-col gap-6 w-full max-w-[800px] mx-auto pb-6 px-2"
      >
        {/* 헤더: 면접유형/관심영역 뱃지 + 날짜 타이틀 (Figma 디자인) */}
        <div className="flex flex-col gap-2 items-start">
          {/* 뱃지 영역 */}
          <div className="flex gap-2 items-center">
            {/* 면접유형 뱃지 - 보라색 배경, 흰색 텍스트 */}
            <div className="bg-[#6d2ef2] flex h-[28px] items-center justify-center min-h-[28px] px-3 rounded-[20px]">
              <span className="text-[14px] font-semibold text-white tracking-[-0.28px] leading-[20px]">
                {result.form_kind}
              </span>
            </div>
            {/* 관심영역 뱃지 - 보라색 테두리, 보라색 텍스트 */}
            <div className="border border-solid border-[#6d2ef2] flex h-[28px] items-center justify-center min-h-[28px] px-3 rounded-[20px]">
              <span className="text-[14px] font-semibold text-[#6d2ef2] tracking-[-0.28px] leading-[20px]">
                {result.inter_area}
              </span>
            </div>
          </div>
          {/* 타이틀 */}
          <h1 className="text-[24px] font-bold text-[#212121] leading-[36px]">
            {formatDate()}
          </h1>
        </div>

        <div className="flex flex-col">
          {/* 구분선 */}
          <div className="h-px w-full bg-[#e2d5fc] mb-6" />

          {/* 기본정보 + 결과요약 섹션 (2열 레이아웃) */}
          <div className="flex flex-col md:flex-row gap-6">
            {/* 기본정보 (Figma 테이블 형식) */}
            <div className="flex-1 flex flex-col gap-3">
              <h2 className="text-[16px] font-bold text-[#212121] leading-[24px] tracking-[-0.32px]">
                기본정보
              </h2>
              {/* 테이블 */}
              <div className="flex flex-col w-full border-b border-solid border-[#e2d5fc]">
                {basicInfoData.map((row, index) => (
                  <div
                    key={index}
                    className="flex w-full border-t border-solid border-[#e2d5fc]"
                  >
                    {/* 헤더 셀 */}
                    <div className="bg-[#f0eafe] flex h-[36px] items-center justify-center min-h-[36px] px-5 py-3 shrink-0 w-[100px]">
                      <span className="text-[12px] text-[#212121] tracking-[-0.24px] leading-[16px] whitespace-nowrap">
                        {row.label}
                      </span>
                    </div>
                    {/* 데이터 셀 */}
                    <div className="flex-1 flex h-[36px] items-center min-h-[36px] px-4 py-2">
                      <span className="text-[14px] font-medium text-[#212121] tracking-[-0.28px] leading-[20px]">
                        {row.value}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 결과요약 (Figma 디자인) */}
            <div className="flex-1 flex flex-col gap-3">
              <h2 className="text-[16px] font-bold text-[#212121] leading-[24px] tracking-[-0.32px]">
                결과요약
              </h2>
              {/* 시간 카드 영역 */}
              <div className="flex gap-3 w-full">
                {/* 총 면접시간 카드 */}
                <div className="flex-1 border border-solid border-[#e2d5fc] rounded-[8px] flex flex-col gap-1 items-center py-4">
                  <span className="text-[12px] text-[#6d2ef2] tracking-[-0.24px] leading-[16px]">
                    총 면접시간
                  </span>
                  <span className="text-[16px] font-bold text-[#6d2ef2] tracking-[-0.32px] leading-[24px]">
                    {formatTime(result.tt_latency)}
                  </span>
                </div>
                {/* 문항별 평균 답변 시간 카드 */}
                <div className="flex-1 border border-solid border-[#e2d5fc] rounded-[8px] flex flex-col gap-1 items-center py-4">
                  <span className="text-[12px] text-[#212121] tracking-[-0.24px] leading-[16px]">
                    문항별 평균 답변 시간
                  </span>
                  <span className="text-[16px] font-bold text-[#212121] tracking-[-0.32px] leading-[24px]">
                    {formatTime(result.avg_latency)}
                  </span>
                </div>
              </div>

              {/* 세로 테이블 (평균 글자 수, 답변 수, TOP3 단어) */}
              <div className="flex flex-col gap-1">
                <p className="text-[12px] text-[#888] tracking-[-0.24px] leading-[16px] text-right w-full">
                  *문항별 분석
                </p>
                <div className="flex w-full">
                  {/* 평균 글자 수 */}
                  <div className="flex-1 flex flex-col">
                    <div className="bg-[#f0eafe] border-t border-r border-solid border-[#e2d5fc] flex h-[36px] items-center justify-center min-h-[36px] px-5 py-3">
                      <span className="text-[12px] text-[#212121] tracking-[-0.24px] leading-[16px] whitespace-nowrap">
                        평균 글자 수
                      </span>
                    </div>
                    <div className="flex-1 border-b border-r border-solid border-[#e2d5fc] flex items-center justify-center min-h-[36px] px-4 py-3">
                      <span className="text-[14px] font-medium text-[#212121] tracking-[-0.28px] leading-[20px] text-center">
                        {result.avg_words}자
                      </span>
                    </div>
                  </div>
                  {/* 답변 수 */}
                  <div className="flex-1 flex flex-col">
                    <div className="bg-[#f0eafe] border-t border-r border-solid border-[#e2d5fc] flex h-[36px] items-center justify-center min-h-[36px] px-5 py-3">
                      <span className="text-[12px] text-[#212121] tracking-[-0.24px] leading-[16px] whitespace-nowrap">
                        답변 수
                      </span>
                    </div>
                    <div className="flex-1 border-b border-r border-solid border-[#e2d5fc] flex items-center justify-center min-h-[36px] px-4 py-3 gap-1">
                      <span className="text-[14px] font-medium text-[#212121] tracking-[-0.28px] leading-[20px]">
                        {result.tt_ans_cnt}개
                      </span>
                      <span className="text-[10px] font-medium text-[#888] tracking-[-0.2px] leading-[16px]">
                        /{result.tt_ques_cnt}문항
                      </span>
                    </div>
                  </div>
                  {/* TOP3 단어 */}
                  <div className="flex-1 flex flex-col">
                    <div className="bg-[#f0eafe] border-t border-solid border-[#e2d5fc] flex h-[36px] items-center justify-center min-h-[36px] px-5 py-3">
                      <span className="text-[12px] text-[#212121] tracking-[-0.24px] leading-[16px] whitespace-nowrap">
                        TOP3 단어
                      </span>
                    </div>
                    <div className="flex-1 border-b border-solid border-[#e2d5fc] flex flex-wrap items-center justify-center min-h-[36px] px-4 py-3 gap-1">
                      {result.top_word_list &&
                      result.top_word_list.length > 0 ? (
                        result.top_word_list.map(
                          (word: string, idx: number) => (
                            <React.Fragment key={idx}>
                              <span className="text-[14px] font-medium text-[#212121] tracking-[-0.28px] leading-[20px] text-center">
                                {word}
                              </span>
                              {idx < result.top_word_list.length - 1 && (
                                <span className="w-px h-3 bg-[#ccc]" />
                              )}
                            </React.Fragment>
                          )
                        )
                      ) : (
                        <span className="text-[14px] font-medium text-[#212121]">
                          -
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 종합결과 섹션 */}
          <div className="flex flex-col gap-3 mt-8">
            <h2 className="text-[16px] font-bold text-[#212121] leading-[24px] tracking-[-0.32px]">
              종합결과
            </h2>
            <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
              {/* 레이더 차트 */}
              <div className="w-full md:flex-1 flex justify-center">
                <CustomChart
                  options={config}
                  data={chartData}
                  type="radar"
                  width={400}
                  height={300}
                />
              </div>

              {/* 평가 항목 드롭다운 리스트 */}
              <div className="flex-1 flex flex-col w-full md:w-auto">
                {arrData.map((item) => {
                  if (!item.score) return null;
                  const isExpanded = expandedEval[item.target];

                  return (
                    <div
                      key={item.target}
                      className="flex flex-col border-t border-solid border-[#E2D5FC] last:border-b"
                    >
                      {/* 드롭다운 헤더 */}
                      <button
                        onClick={() => toggleEval(item.target)}
                        className={`w-full flex items-center justify-between min-h-[36px] py-2 px-2 transition-colors ${
                          isExpanded
                            ? "bg-[#F0EAFE]"
                            : "bg-transparent hover:bg-[#F9F9F9]"
                        }`}
                      >
                        <span
                          className={`text-[16px] transition-colors text-[#C5ABFA] font-bold}`}
                        >
                          {item.target}
                        </span>
                        <Chevron
                          className={`w-6 h-6 transition-transform ${
                            isExpanded ? "-rotate-90" : "rotate-90"
                          }`}
                          style={{ fill: "#C5ABFA" }}
                        />
                      </button>

                      {/* 드롭다운 내용 */}
                      {isExpanded &&
                        item.feedbacks &&
                        item.feedbacks.length > 0 && (
                          <div className="flex flex-col gap-8 p-6 bg-white">
                            {item.feedbacks.map((fb, idx) => {
                              // API level (A/B/C)을 한글로 변환
                              const fbLevel = fb.level
                                ? convertLevel(fb.level)
                                : item.evaluation;
                              const badgeStyle =
                                BADGE_STYLES[
                                  fbLevel as keyof typeof BADGE_STYLES
                                ] || BADGE_STYLES["보통"];

                              return (
                                <div key={idx} className="flex flex-col gap-2">
                                  {/* 상단: 제목 + 점수 + 배지 */}
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <span className="text-[16px] font-bold text-[#212121]">
                                        {fb.name}
                                      </span>
                                      <span className="text-[16px] text-[#888] font-normal">
                                        | {fb.score}점
                                      </span>
                                    </div>
                                    {/* 뱃지: 피그마 디자인 */}
                                    <span
                                      className="px-[12px] h-[24px] min-h-[24px] rounded-[20px] text-[12px] font-semibold flex items-center justify-center leading-none"
                                      style={badgeStyle}
                                    >
                                      {fbLevel}
                                    </span>
                                  </div>

                                  {/* 설명 텍스트 */}
                                  <p className="text-[14px] text-[#666666] leading-[22px]">
                                    {fb.feedback.split("\n")[0]}
                                  </p>

                                  {/* 상세 피드백 회색 박스 */}
                                  <div className="bg-[#F7F7F7] rounded-[8px] p-4 mt-1">
                                    <ul className="flex flex-col gap-1">
                                      {/* Evidence 배열 렌더링 */}
                                      {fb.evidence &&
                                        fb.evidence.length > 0 &&
                                        fb.evidence.map((ev, evIdx) => (
                                          <li
                                            key={evIdx}
                                            className="flex gap-2 text-[13px] text-[#666] leading-[20px]"
                                          >
                                            <span className="shrink-0">•</span>
                                            <span>{ev}</span>
                                          </li>
                                        ))}
                                    </ul>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* 질문과 답변 상세 섹션 (피그마 디자인) */}
          {qaPairs.length > 0 && (
            <div className="flex flex-col gap-[12px] mt-8 sm:mt-0">
              <h2 className="text-[16px] font-bold text-[#212121] leading-[24px] tracking-[-0.32px]">
                질문과 답변 상세
              </h2>
              <div className="flex flex-wrap gap-[12px]">
                {qaPairs.map(
                  (
                    qa: {
                      question: string;
                      answer: string;
                      feedback?: string;
                      idealAnswer: string;
                    },
                    idx: number
                  ) => (
                    <div
                      key={idx}
                      className="flex-1 min-w-[300px] flex flex-col bg-white border border-solid border-[#eee] rounded-[20px] overflow-hidden"
                      style={{
                        boxShadow:
                          "0px 6px 12px -6px rgba(0,0,0,0.04), 0px 8px 24px -4px rgba(0,0,0,0.04)",
                      }}
                    >
                      {/* 상단 영역: 번호, 질문, 답변, 피드백 */}
                      <div className="flex-1 flex flex-col gap-[20px] px-[16px] py-[20px]">
                        {/* Q-A 영역 */}
                        <div className="flex flex-col gap-[12px]">
                          {/* 질문 영역 */}
                          <div className="flex flex-col gap-[8px]">
                            {/* 번호 뱃지 */}
                            <div className="w-[20px] h-[20px] bg-[#e2d5fc] rounded-[10px] flex items-center justify-center">
                              <span className="text-[14px] font-medium text-white leading-[16px] tracking-[-0.28px]">
                                {idx + 1}
                              </span>
                            </div>
                            {/* 질문 텍스트 */}
                            <p className="text-[14px] font-bold text-[#212121] leading-[20px] tracking-[-0.28px]">
                              {qa.question}
                            </p>
                          </div>
                          {/* 답변 텍스트 */}
                          <p className="text-[12px] font-medium text-[#666] leading-[16px] tracking-[-0.24px]">
                            {qa.answer}
                          </p>
                        </div>

                        {/* AI 피드백 영역 */}
                        {qa.feedback && (
                          <div className="flex gap-[4px] items-center">
                            {/* 캐릭터 아이콘 */}
                            <div className="w-[40px] h-[40px] p-[4px] shrink-0">
                              <div className="w-full h-full rounded-full border border-solid border-[#e2d5fc] overflow-hidden">
                                <img
                                  src={iconTeacherTop}
                                  alt="AI 피드백"
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            </div>
                            {/* 피드백 텍스트 */}
                            <p className="flex-1 text-[12px] font-medium text-[#6d2ef2] leading-[16px] tracking-[-0.24px]">
                              {qa.feedback}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* 모범 답변 영역 */}
                      {qa.idealAnswer && (
                        <div className="bg-[#f7f7f7] px-[16px] py-[20px]">
                          <p className="text-[12px] text-[#888] leading-[16px] tracking-[-0.24px]">
                            <span className="font-bold">모범 답변 ㅣ </span>
                            <span className="font-normal">
                              {qa.idealAnswer}
                            </span>
                          </p>
                        </div>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
          )}
        </div>

        {/* 구분선 */}
        <div className="h-px w-full bg-[#e2d5fc]" />

        {/* 하단 버튼 영역 - 피그마 디자인 (여백 없음) */}
        <div className="flex flex-nowrap gap-3 w-full">
          {/* 메인으로 버튼 */}
          <button
            onClick={() => (window.location.href = "/socrates")}
            className="flex-1 h-[48px] min-h-[48px] min-w-0 px-4 rounded-[24px] border border-solid border-[#ccc] bg-white flex items-center justify-center cursor-pointer"
          >
            <span className="text-[14px] font-medium text-[#666] tracking-[-0.28px] leading-[20px] whitespace-nowrap">
              메인으로
            </span>
          </button>
          {/* 나의 활동 보기 버튼 */}
          <Btn.Primary
            onClick={() => (window.location.href = "/my-activity")}
            style="fill"
            color="main-gr"
            className="flex-1 !w-auto !max-w-none !min-w-0 !px-4 !text-[14px]"
          >
            나의 활동 보기
          </Btn.Primary>
        </div>
      </div>
    </>
  );
};

export default InterviewResultV2;
